<!-- Read-only LIVE design viewer — renders a design the way the editor's
     "Run" mode does (DesignCanvas non-interactive + per-shape effects polled
     from design_render), but standalone by id. Used to show designs inline
     in the chat (MCP App, Phase 2) with live values. Pure same-origin Vue, so
     data flows through /api (tunnel-friendly) — no iframe/sandbox/DOM-patching. -->
<template>
  <div class="syn-live">
    <DesignCanvas
      v-if="doc"
      :shapes="panels"
      :selected-ids="[]"
      :frame="frame"
      :interactive="false"
      :effects="liveEffects"
      :forced="forcedState"
      :fit="true"
      :asset-base="assetBase"
      :asset-token="assetToken"
      @trigger="onTrigger"
      @control="onControl"
      @plugin-set-var="onPluginSetVar"
    />
    <div v-else class="ph">{{ error ?? 'Loading design…' }}</div>
    <SynValuePrompt ref="valuePrompt" />
  </div>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref } from 'vue';
import DesignCanvas from './DesignCanvas.vue';
import SynValuePrompt from './SynValuePrompt.vue';
import type { RectPlacement } from './opmodel';
import { useDesigner } from './useDesigner';
import { useDesignRuntime, type RenderShape } from './useDesignRuntime';
import { callTool } from '../api/client';
import { provideDocumentTheme } from './useTheme';

const props = defineProps<{ designId: string }>();
// Internal navigation (a 'navigate: design:<id>' button) bubbles up so the host
// can switch the inline content (e.g. the chat dock re-renders the new design in
// place) instead of this view deciding to open a tab.
const emit = defineEmits<{ navigate: [{ kind: 'design' | 'dashboard'; id: string }] }>();

const d = useDesigner(props.designId, {
  getTool: 'design_get_document', applyTool: 'design_apply_ops', idArg: 'design_id',
});
const { doc, panels } = d;
provideDocumentTheme(computed(() => doc.value?.themeTokens));
const frame = computed<RectPlacement | undefined>(
  () => (doc.value?.root.props as { frame?: RectPlacement } | undefined)?.frame,
);

// Same-origin asset reads (/api/assets, proxied) authorised by a short token.
const assetBase = (import.meta.env.VITE_API_BASE as string | undefined) ?? '';
const assetToken = ref('');
const error = ref<string | null>(null);
const valuePrompt = ref<{ open: (o: { kind?: 'text' | 'number'; label?: string; initial?: string; unit?: string; min?: number; max?: number }) => Promise<string | null> } | null>(null);

// ── Run-mode interaction (clicks) ─────────────────────────────────────
// The shared runtime evaluates event actions client-side, exactly like the
// editor's Run mode. Parametrization for the chat view: authenticated
// callTool transport, external URLs in a NEW TAB (never hijack the host portal),
// internal design navigation BUBBLES UP (host swaps the inline content), and NO
// write capability — 'command' actions and writeBack controls are inert here.
const rt = useDesignRuntime({
  nodes: () => panels.value,
  render: (vars) => callTool<{ shapes?: RenderShape[] }>('design_render', { design_id: props.designId, vars, tz: Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC' }),
  prompt: (o) => valuePrompt.value?.open(o),
  navigate: {
    external: (url) => window.open(url, '_blank', 'noopener'),
    internal: (target) => emit('navigate', target),
  },
});
const { liveEffects, forcedState, onTrigger, onControl, onPluginSetVar, tick } = rt;

onMounted(async () => {
  try {
    await d.load();
  } catch (e) {
    error.value = e instanceof Error ? e.message : String(e);
    return;
  }
  try {
    const r = await callTool<{ token?: string }>('designer_live_token', { doc_id: props.designId });
    assetToken.value = r.token ?? '';
  } catch { /* assets best-effort */ }
  // Subscribe to the doc channel: when the assistant (or anyone) edits this
  // design — design_apply_ops broadcasts the ops — useDesigner applies them
  // to the reactive doc, so the inline view re-renders the new layout LIVE,
  // no need to re-ask "show it again". Re-evaluate effects after each edit too.
  void d.connectLive();
  d.onRemoteOps.value = () => { void tick(); };
  await tick();
  rt.startPolling(3000);
});
onBeforeUnmount(() => {
  d.disconnectLive();
});
</script>

<style scoped>
/* Flex container with a definite height (from the parent) so DesignCanvas's
 * `.syn-wrap` (flex:1) fills it — otherwise its height collapses to ~0 and the
 * fit-zoom shrinks the frame to a sliver. */
.syn-live { flex: 1; display: flex; min-width: 0; min-height: 0; width: 100%; height: 100%; background: var(--color-surface-2); }
.syn-live :deep(.syn-wrap) { flex: 1; min-width: 0; min-height: 0; }
.ph { display: flex; align-items: center; justify-content: center; width: 100%; height: 100%; color: var(--color-faint); font-size: 0.8rem; }
</style>
